import React, { Ref, forwardRef, useRef } from 'react';
import { usePatternFormat, NumberFormatBase } from 'react-number-format';

import {
  ChangeMeta,
  FormatInputValueFunction, NumberFormatBaseProps, NumberFormatValues, PatternFormatProps, RemoveFormattingFunction,
} from 'react-number-format/types/types';

import { isFunction, isUndefined } from '@app/common/utils';
import { mask as maskString } from '@app/common/utils/string/mask/mask';
import { reconcile } from '@app/common/utils/string/reconcile/reconcile';

export type MaskedNumberInputValues = Pick<NumberFormatValues, 'value' | 'formattedValue'>;

export type MaskedNumberInputProps = {
  /**
   * Pattern for masking (hide) some characters in input
   * @example '***-**-0000'
   * @default ''
   */
  mask?: string;

  /**
   * Mask pattern char, used to identify characters that should be masked
   * @default '*'
   */
  maskPatternChar?: string;

  /**
   * Mask char, used to replace characters that should be masked
   * @default '•'
   */
  maskChar?: string;

  /**
   * The callback that is called when the value is changed
   */
  onValueChange?: (values: MaskedNumberInputValues) => void;

  /**
   * Label for input used as placeholder
   */
  label?: string | null;
} & PatternFormatProps;

const MASK_PATTERN_CHAR = '*';
const MASK_CHAR = '•';
const PATTERN_CHAR = '0';

/**
 * The component that extends react-number-format component with masking (hiding) some numbers in input
 */
export const MaskedNumberInput = forwardRef<Ref<NumberFormatBaseProps>, MaskedNumberInputProps>(({
  mask = '',
  maskPatternChar = MASK_PATTERN_CHAR,
  maskChar = MASK_CHAR,
  patternChar = PATTERN_CHAR,
  onValueChange,
  ...patternFormatProps
}, ref) => {
  const { format, removeFormatting, ...fieldProps } = usePatternFormat({
    ...patternFormatProps,
    patternChar,
  });
  const masked = useRef<string>('');
  const formatted = useRef<string>('');
  const currentValue = useRef<string>('');
  const lastValue = useRef<string>('');
  const lastChangeMeta = useRef<ChangeMeta>();

  // returns formatted and masked value
  const formatInternal: FormatInputValueFunction = (val) => {
    const formattedVal = isFunction(format) ? format(val) : val;
    const maskedVal = maskString(formattedVal, mask, {
      maskPatternChar,
      maskChar,
    });

    formatted.current = formattedVal;
    masked.current = maskedVal;
    return masked.current;
  };

  // returns unformatted and unmasked value
  const removeFormattingInternal: RemoveFormattingFunction = (val, changeMeta) => {
    // keep change meta to use it for further caret position
    if (!isUndefined(changeMeta)) {
      lastChangeMeta.current = changeMeta;
    }

    const reconciledVal = reconcile(
      masked.current,
      val,
      formatted.current,
    );

    const output = removeFormatting!(reconciledVal || val);
    currentValue.current = output;
    return output;
  };

  const onChange: React.ChangeEventHandler<HTMLInputElement> = ({
    target,
    nativeEvent,
  }) => {
    const eventType = (nativeEvent as InputEvent).inputType;
    if (eventType === 'insertText') {
      // pattern format does not support moving caret position for masked characters
      // so here we check if last changed character is masked and move caret to next empty space
      if (target.selectionStart !== null && target.value[target.selectionStart] === maskChar) {
        // need to make it async to move caret after the character is changed
        setTimeout(() => {
          // eslint-disable-next-line no-param-reassign
          target.selectionStart! += 1;
          // eslint-disable-next-line no-param-reassign
          target.selectionEnd = target.selectionStart;
        }, 0);
      }
    } else if (eventType === 'insertFromPaste') {
      // move caret to next empty space
      if (target.selectionStart != null) {
        // eslint-disable-next-line no-param-reassign
        target.selectionStart = target.value.indexOf(' ');
        // eslint-disable-next-line no-param-reassign
        target.selectionEnd = target.selectionStart;
      }
    }

    // OnValueChange handling.
    // We cant use pattern format onValueChange from the box,
    // because it does not trigger when only masked characters are changed.

    // if value is not changed - do not trigger onValueChange callback
    if (currentValue.current !== lastValue.current) {
      lastValue.current = currentValue.current;

      if (isFunction(onValueChange)) {
        onValueChange({
          value: currentValue.current,
          formattedValue: formatted.current,
        });
      }
    }
  };

  return (
    <NumberFormatBase
      {...fieldProps}
      format={formatInternal}
      getInputRef={ref}
      removeFormatting={removeFormattingInternal}
      onChange={(e) => {
        if (isFunction(fieldProps.onChange)) {
          fieldProps.onChange(e);
        }
        onChange(e);
      }}
    />
  );
});
